import { Injectable } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { firstValueFrom } from 'rxjs';

export interface ChunkUploadProgress {
	percent: number;
	uploadedBytes: number;
	totalBytes: number;
}

/**
 * Parallel chunk uploader for large files (typically videos).
 *
 * Instead of uploading the entire file over a single HTTP connection, this
 * service slices the file into 8 MB chunks and uploads up to 4 chunks
 * concurrently via signed PUT URLs, then asks the backend Cloud Function to
 * compose the chunks into the final GCS object.
 *
 * Usage:
 *   const url = await parallelUploadService.uploadFileInParallel(
 *       file, 'programs/xyz/videos/video-0-0-0.mp4', (p) => this.progress = p.percent
 *   );
 */
@Injectable({
	providedIn: 'root'
})
export class ParallelUploadService {
	/** Each chunk is 16 MB. */
	private readonly CHUNK_SIZE = 16 * 1024 * 1024;

	/** Max simultaneous chunk uploads. Browsers allow up to 6 connections per host. */
	private readonly CONCURRENCY = 6;

	constructor(private functions: AngularFireFunctions) {}

	/**
	 * Upload a file in parallel chunks to Google Cloud Storage.
	 *
	 * @param file             The File/Blob to upload.
	 * @param destinationPath  Target GCS object path (e.g. "programs/xyz/videos/video-0-0-0.mp4").
	 * @param onProgress       Callback invoked with overall upload progress.
	 * @returns                Public download URL of the composed object.
	 */
	async uploadFileInParallel(
		file: File,
		destinationPath: string,
		onProgress: (progress: ChunkUploadProgress) => void
	): Promise<string> {
		const totalBytes = file.size;
		const totalChunks = Math.ceil(totalBytes / this.CHUNK_SIZE);

		console.log(`[ParallelUpload] Slicing "${file.name}" (${(totalBytes / (1024 * 1024)).toFixed(1)} MB) into ${totalChunks} chunks (${this.CHUNK_SIZE / (1024 * 1024)} MB each)...`);
		const startTime = Date.now();

		// 1. Ask the backend for signed URLs for each chunk.
		const generateUrls = this.functions.httpsCallable('upload-generateSignedUploadUrls');
		const initResponse: any = await firstValueFrom(generateUrls({
			destinationPath,
			contentType: file.type || 'video/mp4',
			totalChunks
		}));

		const { sessionId, signedUrls } = initResponse;
		console.log(`[ParallelUpload] Obtained signed URLs for session ${sessionId}. Uploading ${totalChunks} chunks (up to ${this.CONCURRENCY} concurrently)...`);

		// 2. Build upload tasks — one per chunk.
		const uploadedBytesPerChunk = new Map<number, number>();
		let maxReportedPercent = 0;

		const emitProgress = () => {
			let uploaded = 0;
			uploadedBytesPerChunk.forEach((v) => (uploaded += v));
			const calculated = Math.min(99, Math.round((uploaded / totalBytes) * 100));
			if (calculated >= maxReportedPercent) {
				maxReportedPercent = calculated;
				onProgress({
					percent: maxReportedPercent,
					uploadedBytes: uploaded,
					totalBytes
				});
			}
		};

		const tasks: (() => Promise<void>)[] = [];

		for (let i = 0; i < totalChunks; i++) {
			const start = i * this.CHUNK_SIZE;
			const end = Math.min(start + this.CHUNK_SIZE, totalBytes);
			const chunkBlob = file.slice(start, end);
			const { url } = signedUrls[i];

			tasks.push(() =>
				this.uploadChunk(chunkBlob, url, file.type || 'video/mp4', (loaded) => {
					uploadedBytesPerChunk.set(i, loaded);
					emitProgress();
				})
			);
		}

		// 3. Execute with concurrency limit.
		await this.runPool(tasks, this.CONCURRENCY);
		const uploadElapsed = ((Date.now() - startTime) / 1000).toFixed(1);
		console.log(`[ParallelUpload] All ${totalChunks} chunks uploaded in ${uploadElapsed}s! Composing final object...`);

		// 4. Ask the backend to compose chunks into the final object.
		const completeUpload = this.functions.httpsCallable('upload-completeChunkedUpload', {
			timeout: 300_000 // 5 minutes — composition of many chunks can take a moment
		});
		const completeResponse: any = await firstValueFrom(completeUpload({
			sessionId,
			destinationPath,
			totalChunks,
			contentType: file.type || 'video/mp4'
		}));

		const totalElapsed = ((Date.now() - startTime) / 1000).toFixed(1);
		console.log(`[ParallelUpload] Video upload & composition completed in ${totalElapsed}s!`, completeResponse.downloadUrl);

		onProgress({ percent: 100, uploadedBytes: totalBytes, totalBytes });

		return completeResponse.downloadUrl;
	}

	// ─── Private helpers ───────────────────────────────────────────────

	/**
	 * Upload a single chunk via XMLHttpRequest so we get fine-grained progress.
	 */
	private uploadChunk(
		chunk: Blob,
		signedUrl: string,
		contentType: string,
		onChunkProgress: (loadedBytes: number) => void
	): Promise<void> {
		return new Promise((resolve, reject) => {
			const xhr = new XMLHttpRequest();
			xhr.open('PUT', signedUrl, true);
			xhr.setRequestHeader('Content-Type', contentType);

			xhr.upload.onprogress = (e: ProgressEvent) => {
				if (e.lengthComputable) {
					onChunkProgress(e.loaded);
				}
			};

			xhr.onload = () => {
				if (xhr.status >= 200 && xhr.status < 300) {
					onChunkProgress(chunk.size); // ensure final byte count is exact
					resolve();
				} else {
					reject(new Error(`Chunk upload failed: HTTP ${xhr.status} — ${xhr.statusText}`));
				}
			};

			xhr.onerror = () => reject(new Error('Network error during chunk upload.'));
			xhr.ontimeout = () => reject(new Error('Chunk upload timed out.'));

			xhr.send(chunk);
		});
	}

	/**
	 * Concurrency-limited task pool.
	 *
	 * Runs up to `concurrency` tasks at the same time.  As soon as one
	 * finishes, the next queued task starts.
	 */
	private async runPool(tasks: (() => Promise<void>)[], concurrency: number): Promise<void> {
		let index = 0;
		const executing: Set<Promise<void>> = new Set();

		const enqueue = async (): Promise<void> => {
			if (index >= tasks.length) return;

			const taskFn = tasks[index++];
			const promise = taskFn().then(() => {
				executing.delete(promise);
			});
			executing.add(promise);

			if (executing.size >= concurrency) {
				await Promise.race(executing);
			}

			return enqueue();
		};

		// Start `concurrency` workers.
		const workers: Promise<void>[] = [];
		for (let w = 0; w < Math.min(concurrency, tasks.length); w++) {
			workers.push(enqueue());
		}
		await Promise.all(workers);

		// Wait for any remaining in-flight tasks.
		if (executing.size > 0) {
			await Promise.all(executing);
		}
	}
}
